import { useSelector } from "react-redux";
import type { FilterDialogComponentProps as CoreFilterDialogComponentProps } from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type SortOptionEnum,
  TripCategory,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterAirport,
  FilterAirline,
  FilterFareClass,
  FilterStops,
  FilterTime,
  FilterFlightNumber,
} from "@tenant/flights";
import {
  FilterDialogContainer,
  FilterDialogSection,
  FilterPrice,
} from "@tenant/ui";
import clsx from "clsx";
import { FilterListSort } from "../FilterListSort";
import defaultStyles from "./FilterDialog.module.scss";
import { useFilterDialog } from "./useFilterDialog";
import { getPricingMode } from "@/store/flights/shop/store";

export interface FilterDialogComponentProps
  extends CoreFilterDialogComponentProps {
  sortOption?: SortOptionEnum;
  handleSortChange?: (value: SortOptionEnum) => void;
}

export const FilterDialog = ({
  sortOption,
  handleSortChange,
  sliceIndex,
  children,
  className,
  applyAirlineFiltersToFlightNumber,
}: FilterDialogComponentProps) => {
  const pricingMode = useSelector(getPricingMode);
  const { t } = useI18nContext();

  const styles = useFlightStyles(
    CoreFlightsComponent.FilterDialog,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FilterDialog, [
    pricingMode,
  ]);

  // Use RBC custom hook that includes airport filter
  const {
    context: {
      fareClasses,
      stop,
      filterOptions,
      airline,
      airport,
      origin,
      destination,
      outboundDepartureTimeRange,
      outboundArrivalTimeRange,
      returnDepartureTimeRange,
      returnArrivalTimeRange,
      tripCategory,
      priceState,
      flightNumbersByAirline,
      flightNumber,
    },
    handlers: {
      handleFareClassesChange,
      handleAirlineChange,
      handleAirportChange,
      handleOutboundTimeRangeChange,
      handleReturnTimeRangeChange,
      handlePriceChange,
      handleFlightNumberChange,
      handleStopsChange,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
    },
  } = useFilterDialog({ sliceIndex, applyAirlineFiltersToFlightNumber });

  // Only show sort on mobile when both props are provided
  const showSort = sortOption !== undefined && handleSortChange !== undefined;

  return (
    <FilterDialogContainer
      onClickDialogTrigger={updateValuesToMatchState}
      onClickReset={resetAllFilters}
      onClickApply={applyFilter}
      className={clsx(className, block)}
    >
      <FilterDialogSection
        title={t("searchFilter.fareClass")}
        content={
          <FilterFareClass
            fareClasses={fareClasses}
            onFareClassChange={handleFareClassesChange}
          />
        }
      />
      <FilterDialogSection
        title={t("searchFilter.stops")}
        content={
          <FilterStops value={stop} onStopFilterChange={handleStopsChange} />
        }
      />
      <FilterDialogSection
        title={t("searchFilter.airline")}
        content={
          <FilterAirline
            allAirlines={filterOptions.airlineOptions}
            airlineFilter={airline}
            onAirlineFilterChange={handleAirlineChange}
          />
        }
      />
      {filterOptions.airportOptions.length > 2 && (
        <FilterDialogSection
          title={t("searchFilter.airport")}
          content={
            <FilterAirport
              allAirports={filterOptions.airportOptions}
              airportFilter={airport}
              onAirportFilterChange={handleAirportChange}
            />
          }
        />
      )}
      {sliceIndex === 0 ? (
        <FilterDialogSection
          title={t("core-flights.searchFilter.outboundTimes")}
          content={
            <FilterTime
              origin={origin}
              destination={destination}
              departureTimeRange={outboundDepartureTimeRange}
              arrivalTimeRange={outboundArrivalTimeRange}
              onSliceTimeRangeChange={handleOutboundTimeRangeChange}
            />
          }
        />
      ) : null}
      {tripCategory === TripCategory.ROUND_TRIP ? (
        <FilterDialogSection
          title={t("core-flights.searchFilter.returnTimes")}
          content={
            <FilterTime
              origin={destination}
              destination={origin}
              departureTimeRange={returnDepartureTimeRange}
              arrivalTimeRange={returnArrivalTimeRange}
              onSliceTimeRangeChange={handleReturnTimeRangeChange}
            />
          }
        />
      ) : null}
      <FilterDialogSection
        title={t("searchFilter.maxPrice")}
        subtitle={
          tripCategory === TripCategory.ROUND_TRIP
            ? t("searchFilter.maxPriceRoundTripSubtitle")
            : t("searchFilter.maxPriceOneWaySubtitle")
        }
        className={cn("price")}
        content={
          <FilterPrice
            className={cn("filter-popover")}
            label={t("searchFilter.maxPrice")}
            minimumPrice={filterOptions.priceMin}
            maximumPrice={filterOptions.priceMax}
            initialValue={{
              minPrice: filterOptions.priceMin?.value ?? 0,
              maxPrice: priceState,
            }}
            onFilterPriceChange={handlePriceChange}
          />
        }
      />
      <FilterDialogSection
        title={t("searchFilter.flightNumber")}
        subtitle={t("searchFilter.flightNumberSubtitle")}
        content={
          <FilterFlightNumber
            flightNumbersByAirline={flightNumbersByAirline}
            onFlightNumberChange={handleFlightNumberChange}
            selectedFlightNumber={flightNumber}
          />
        }
      />
      {showSort && (
        <FilterDialogSection
          className={cn("sort")}
          title={t("searchFilter.sortBy")}
          content={
            <FilterListSort
              selectedSort={sortOption}
              onChange={handleSortChange}
            />
          }
        />
      )}
      {children}
    </FilterDialogContainer>
  );
};
